import type { DrawingDocument } from '../model';
import { isChemicalBond } from './documentSelectors.ts';

// ADR 0026 D7 (#1158) -- a three-centre two-electron bond, stored as two single bonds to the
// bridging hydrogen, as PubChem and InChI have diborane ([BH2]1[H][BH2][H]1). A hydrogen with two
// bonds is such a bridge when both its neighbours are boron, aluminium, gallium or a metal. RDKit
// rejects it, so the engines get each bridge bond as a zero-order bond and PeruseLab counts the
// hydrogens of the atoms it bridges (BridgingHydrogens on the server does the same for CDK).

/** Boron, aluminium and gallium: their hydrogens are counted with each bridge as half a bond. */
const GROUP_13 = new Set([5, 13, 31]);
/** Beryllium and magnesium form hydride bridges too (BeH2, Mg(BH4)2). */
const ALKALINE_EARTH = new Set([4, 12]);

/** Whether an element can hold a hydrogen bridge: B, Al, Ga, Be, Mg, a d- or f-block metal. */
export function isBridgePartner(atomicNumber: number): boolean {
  const z = atomicNumber;
  return (
    GROUP_13.has(z) ||
    ALKALINE_EARTH.has(z) ||
    (z >= 21 && z <= 30) ||
    (z >= 39 && z <= 48) ||
    (z >= 57 && z <= 80) ||
    (z >= 89 && z <= 112)
  );
}

/** The ids of the bonds to bridging hydrogens -- two per bridge. */
export function bridgeBondIds(document: DrawingDocument): ReadonlySet<string> {
  const { atoms, bonds, graph } = document.structure;
  const bondsOf = new Map<string, string[]>();
  for (const edge of graph.edges) {
    if (!isChemicalBond(bonds[edge.id])) continue;
    for (const end of [edge.source, edge.target]) bondsOf.set(end, [...(bondsOf.get(end) ?? []), edge.id]);
  }
  const edgeById = new Map(graph.edges.map((edge) => [edge.id, edge]));
  const ids = new Set<string>();
  for (const [atomId, edgeIds] of bondsOf) {
    if (atoms[atomId]?.atomicNumber !== 1 || edgeIds.length !== 2) continue;
    const bridging = edgeIds.every((edgeId) => {
      const bond = bonds[edgeId]!;
      const edge = edgeById.get(edgeId)!;
      const other = edge.source === atomId ? edge.target : edge.source;
      return bond.order === 1 && bond.type !== 'dative' && isBridgePartner(atoms[other]?.atomicNumber ?? 0);
    });
    if (bridging) edgeIds.forEach((edgeId) => ids.add(edgeId));
  }
  return ids;
}

/** Whether the element counts each bridge as half a bond for its hydrogens (B, Al, Ga). */
export function countsBridgesAsHalf(atomicNumber: number): boolean {
  return GROUP_13.has(atomicNumber);
}
